import { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { fpBase, inr } from "@/lib/fp";
import { toast } from "sonner";

const AREA_LABEL = {
  revenue: "Revenue", pvc: "Production variable cost", dvc: "Distribution variable cost",
  fixed_cost: "Fixed cost", net_fg_stock: "Net FG stock", other: "Other",
};

/** One-time workbook: the client's own mapped P&L, which classifies every ledger for every month. */
export const FpClassificationMaster = ({ tenantId, canConfigure }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const send = async (what, chosen) => {
    const picked = chosen || file;
    if (!picked) return;
    const body = new FormData();
    body.append("file", picked);
    if (what === "apply") body.append("replace", "true");
    setBusy(what);
    try {
      const { data } = await api.post(
        `${fpBase(tenantId)}/classification-master/${what}`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (what === "preview") {
        setPreview(data);
        toast.success(`${data.ledgers} ledgers read — nothing written yet`);
      } else {
        toast.success(`${data.classified} ledgers classified · ${data.retired} earlier mappings retired`);
        setPreview(null);
        setFile(null);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className="panel p-6" data-testid="fp-classification-master">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="label-caps">One-time workbook</p>
          <h3 className="font-display text-lg text-navy">Ledger classification master</h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/75">
            One mapped P&amp;L export — every ledger tagged with its heading, business function,
            production category, sub-channel and component — becomes the single source of truth for
            <strong> every month</strong>. Upload it once here; each month's own export is then read
            through it, so a ledger never has to be classified twice. Fixed cost, salary included, is
            taken from these tagged ledgers.
          </p>
        </div>
        <div className="text-right">
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
                 data-testid="fp-cm-file"
                 onChange={(e) => {
                   const f = e.target.files?.[0];
                   setFile(f || null);
                   setPreview(null);
                   if (f) send("preview", f);
                 }} />
          <button onClick={() => fileRef.current?.click()} disabled={!canConfigure || !!busy}
                  data-testid="fp-cm-choose"
                  className="rounded-full bg-terracotta px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-white transition hover:bg-terracotta/90 disabled:opacity-50">
            {busy === "preview" ? "Reading" : file ? "Choose another file" : "Upload mapped P&L"}
          </button>
          {file && <p className="mt-1.5 max-w-[15rem] truncate text-[10px] text-muted-foreground"
                      title={file.name}>{file.name}</p>}
          {!canConfigure && (
            <p className="mt-1.5 text-[10px] text-muted-foreground">
              Aikyam Admins only
            </p>
          )}
        </div>
      </div>

      {preview && (
        <div className="mt-5 space-y-4 border-t border-border pt-5" data-testid="fp-cm-preview">
          <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground">
            <span data-testid="fp-cm-count-ledgers">
              <span className="font-semibold text-navy">{preview.ledgers}</span> ledgers classified
            </span>
            <span data-testid="fp-cm-count-unmapped">
              <span className="font-semibold text-[hsl(30_88%_30%)]">{preview.unmapped_rows}</span> rows
              left untagged in the sheet
            </span>
            <span data-testid="fp-cm-count-conflicts">
              <span className={preview.conflicts?.length ? "font-semibold text-destructive"
                                                          : "font-semibold text-success"}>
                {preview.conflicts?.length || 0}</span> conflicts
            </span>
            <span>
              replaces <span className="font-semibold text-navy">{preview.current_approved_mappings}</span> approved
              mappings in force today
            </span>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="label-caps">What it classifies, by area</p>
              <table className="mt-2 w-full text-xs">
                <tbody className="divide-y divide-border">
                  {Object.entries(preview.by_area || {}).map(([area, v]) => (
                    <tr key={area} data-testid={`fp-cm-area-${area}`}>
                      <td className="py-1.5 text-navy">{AREA_LABEL[area] || area}</td>
                      <td className="py-1.5 text-right text-muted-foreground">{v.ledgers}</td>
                      <td className="py-1.5 text-right font-medium text-navy">{inr(v.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <p className="label-caps">Fixed cost by business function</p>
              <table className="mt-2 w-full text-xs">
                <tbody className="divide-y divide-border">
                  {Object.entries(preview.fixed_by_function || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([fn, amount]) => (
                      <tr key={fn} data-testid={`fp-cm-function-${fn}`}>
                        <td className="py-1.5 text-navy">{fn}</td>
                        <td className="py-1.5 text-right font-medium text-navy">{inr(amount)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {preview.conflicts?.length > 0 && (
            <div className="rounded-xl bg-warning/15 p-4" data-testid="fp-cm-conflicts">
              <p className="text-xs font-semibold text-[hsl(30_88%_28%)]">
                The same ledger is tagged two different ways — fix the sheet before applying
              </p>
              <ul className="mt-2 space-y-1 text-[11px] text-navy">
                {preview.conflicts.slice(0, 8).map((c, i) => (
                  <li key={i}>{c.ledger || c.key}: {c.detail || JSON.stringify(c)}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => send("apply")} disabled={!canConfigure || !!busy}
                    data-testid="fp-cm-apply"
                    className="rounded-full bg-navy px-5 py-2 text-[10px] uppercase tracking-[0.12em] text-white transition hover:bg-navy/90 disabled:opacity-50">
              {busy === "apply" ? "Applying" : "Apply as the classification master"}
            </button>
            <button onClick={() => { setPreview(null); setFile(null); }}
                    data-testid="fp-cm-discard"
                    className="rounded-full border border-border px-5 py-2 text-[10px] uppercase tracking-[0.12em] text-navy transition hover:bg-muted">
              Discard
            </button>
            <p className="text-[11px] text-muted-foreground">
              Applying supersedes the mappings in force — nothing is deleted, the earlier set is
              retired with a full audit trail, and every month recomputes on the new classification.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
